// Siggy avatar. The illustrated pharmacist character used wherever Siggy
// "speaks" (chat bubbles, suggestion chips, the trust strip).
//
// V3 (round-3 motion): renders an MP4 loop generated by Veo 2 from the
// matching static portrait. The video is 9:16 (Veo 2 doesn't support
// 1:1) and gets CSS center-cropped into a circle so the head stays
// framed. Three loops exist: idle (breathing + occasional blink),
// thinking (eyes glance up + finger tap), celebrating (head tilt +
// soft confetti).
//
// Accessibility: respects prefers-reduced-motion. When the OS pref
// is "reduce", we render the static PNG instead. Same circle clip,
// same prop API.
//
// V2 had the static PNG; V3 is purely additive (the PNGs stay as the
// poster + reduced-motion fallback). No call sites need to change.

import { useEffect, useState } from 'react';

interface SiggyAvatarProps {
  size?: number;
  expression?: 'default' | 'thinking' | 'celebrating';
  /** Static-only override; default reads the OS prefers-reduced-motion. */
  staticOnly?: boolean;
  'aria-hidden'?: boolean;
}

const VARIANTS: Record<NonNullable<SiggyAvatarProps['expression']>, string> = {
  default: 'siggy',
  thinking: 'siggy-thinking',
  celebrating: 'siggy-celebrating',
};

const LOOP_NAMES: Record<NonNullable<SiggyAvatarProps['expression']>, string> = {
  default: 'siggy-idle-loop',
  thinking: 'siggy-thinking-loop',
  celebrating: 'siggy-celebrating-loop',
};

// Pick the smallest pre-rendered PNG that covers @2x of the requested
// CSS px (poster + reduced-motion fallback).
function pickSize(cssPx: number): number {
  const target = cssPx * 2;
  if (target <= 64) return 64;
  if (target <= 128) return 128;
  if (target <= 256) return 256;
  if (target <= 512) return 512;
  return 1024;
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent): void => setReduced(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return reduced;
}

export function SiggyAvatar({
  size = 40,
  expression = 'default',
  staticOnly,
  ...rest
}: SiggyAvatarProps): JSX.Element {
  const file = VARIANTS[expression];
  const loopFile = LOOP_NAMES[expression];
  const renderSize = pickSize(size);
  const reduced = useReducedMotion();
  const showVideo = !staticOnly && !reduced;

  const wrapperStyle = {
    width: size,
    height: size,
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'inline-block',
    position: 'relative' as const,
    background: '#E3EDF5',
  };

  if (!showVideo) {
    return (
      <img
        src={`/siggy/${file}-${renderSize}.png`}
        width={size}
        height={size}
        alt="Siggy"
        className="siggy-avatar"
        style={{
          ...wrapperStyle,
          objectFit: 'cover' as const,
        }}
        aria-hidden={rest['aria-hidden'] ?? true}
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <span className="siggy-avatar" style={wrapperStyle} aria-hidden={rest['aria-hidden'] ?? true}>
      <video
        src={`/siggy/${loopFile}.mp4`}
        poster={`/siggy/${file}-${renderSize}.png`}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        style={{
          width: '100%',
          height: '100%',
          // 9:16 source center-cropped into a circle. The character's
          // face is composed in the upper-middle of the frame; object-position
          // shifts the visible region slightly up so eyes + glasses sit
          // centered in the circle.
          objectFit: 'cover' as const,
          objectPosition: 'center 30%',
          display: 'block',
        }}
      />
    </span>
  );
}
